import { createSlice } from '@reduxjs/toolkit';
import { apiSliceWithPrefix } from '../api/apiSlice';

// Status constants
export const LINE_STATUSES = {
  ATTRIBUTED: "UP_TO_DATE",
  UNATTRIBUTED: "UNATTRIBUTED", 
  TERMINATED: "CANCELLED",
  BLOCKED: "SUSPENDED",
  PAUSED: "DISCONNECTED",
  WAITING_SIM: "NEEDS_TO_BE_ACTIVATED"
};

// Fonction utilitaire pour vérifier si une ligne résiliée est disponible (plus d'un an)
export const isTerminatedLineAvailable = (terminatedAt) => {
  if (!terminatedAt) return false;
  
  const terminationDate = new Date(terminatedAt);
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  return terminationDate <= oneYearAgo;
};

// Fonction utilitaire pour analyser la disponibilité des comptes
export const analyzeAccountsAvailability = (accounts, agencyId = null) => {
  const filtered = agencyId 
    ? accounts.filter(account => account.agencyId === agencyId) 
    : accounts;
    
  const analyzed = filtered.map(account => {
    const terminatedLines = account.lines?.filter(line => 
      line.status === LINE_STATUSES.TERMINATED && 
      isTerminatedLineAvailable(line.terminatedAt)
    ) || [];
    
    return {
      ...account,
      availableSlots: account.maxLines - account.activeLines,
      terminatedLines: terminatedLines,
      canAcceptNewLine: account.activeLines < account.maxLines
    };
  });
  
  return {
    availableAccounts: analyzed.filter(acc => acc.availableSlots > 0 || acc.terminatedLines.length > 0),
    terminatedLines: analyzed.flatMap(acc => acc.terminatedLines)
  };
};

// Extension de l'API avec les endpoints pour les comptes RED
export const redAccountsApiSlice = apiSliceWithPrefix.injectEndpoints({
  endpoints: (builder) => ({
    // Récupérer tous les comptes RED
    getRedAccounts: builder.query({
      query: () => '/red-accounts',
      providesTags: (result) =>
        result 
          ? [
              ...result?.redAccounts?.map(({ id }) => ({ type: 'RedAccount', id })),
              { type: 'RedAccount', id: 'LIST' }
            ]
          : [{ type: 'RedAccount', id: 'LIST' }]
    }),
    
    // Récupérer les comptes RED par agence
    getRedAccountsByAgency: builder.query({
      query: (agencyId) => `/agencies/${agencyId}/red-accounts`,
      providesTags: (result, error, agencyId) => 
        result 
          ? [
              ...result.map(({ id }) => ({ type: 'RedAccount', id })),
              { type: 'RedAccount', id: `Agency_${agencyId}` }
            ]
          : [{ type: 'RedAccount', id: `Agency_${agencyId}` }]
    }),
    
    // Récupérer un compte RED par ID
    getRedAccountById: builder.query({
      query: (id) => `/red-accounts/${id}`,
      providesTags: (result, error, id) => [{ type: 'RedAccount', id }]
    }),
    
    // Créer un nouveau compte RED
    createRedAccount: builder.mutation({
      query: (accountData) => ({
        url: '/red-accounts',
        method: 'POST',
        body: accountData
      }),
      invalidatesTags: [
        { type: 'RedAccount', id: 'LIST' },
        // Invalider aussi les lignes disponibles car un nouveau compte peut affecter les disponibilités
        { type: 'LineReservation', id: 'AVAILABLE' }
      ]
    }),
    
    // Créer une nouvelle ligne sur un compte
    createLine: builder.mutation({
      query: ({ accountId, lineData }) => ({
        url: `/phones/`,
        method: 'POST',
        body: {
          phoneNumber: lineData.phoneNumber,
          phoneType: 'POSTPAID', // Type par défaut pour les lignes RED
          userId: lineData.clientId || null,
          agencyId: null, // Sera défini par le backend selon l'utilisateur connecté
          simCardId: lineData.simCardId || null,
          redAccountId: accountId,
          orderDate: lineData.orderDate,
          trackingNotes: lineData.trackingNotes,
          isHistoricalLine: lineData.isHistoricalLine || false,
          skipOrderTracking: lineData.skipOrderTracking || false
        }
      }),
      invalidatesTags: (result, error, { accountId }) => {
        console.log('Invalidating tags for createLine:', { result, error, accountId });
        return [
          { type: 'RedAccount', id: accountId },
          { type: 'RedAccount', id: 'LIST' },
          // Invalider aussi les lignes disponibles pour réservation
          { type: 'LineReservation', id: 'AVAILABLE' },
          { type: 'Phone', id: 'LIST' }
        ];
      }
    }),
    
    // Changer le statut d'une ligne
    changeLineStatus: builder.mutation({
      query: ({ accountId, lineId, status }) => ({
        url: `/red-accounts/${accountId}/lines/${lineId}/status`,
        method: 'PATCH',
        body: { status }
      }),
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'RedAccount', id: accountId }
      ]
    }),
    
    // Attribuer une ligne à un client
    assignLine: builder.mutation({
      query: ({ accountId, lineId, clientId, simCardId }) => ({
        url: `/red-accounts/${accountId}/lines/${lineId}/assign`,
        method: 'POST',
        body: { clientId, simCardId }
      }),
      invalidatesTags: (result, error, { accountId, clientId }) => [
        { type: 'RedAccount', id: accountId },
        { type: 'Client', id: clientId }
      ]
    }),
    
    // Résilier une ligne
    terminateLine: builder.mutation({
      query: ({ accountId, lineId, reason }) => ({
        url: `/red-accounts/${accountId}/lines/${lineId}/terminate`,
        method: 'POST',
        body: { reason }
      }),
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'RedAccount', id: accountId }
      ]
    }),
    
    // Commander une carte SIM pour une ligne
    orderSimCard: builder.mutation({
      query: ({ accountId, lineId, clientId }) => ({
        url: `/red-accounts/${accountId}/lines/${lineId}/order-sim`,
        method: 'POST',
        body: { clientId }
      }),
      invalidatesTags: (result, error, { accountId, clientId }) => [
        { type: 'RedAccount', id: accountId },
        { type: 'Client', id: clientId },
        { type: 'SimCard', id: 'LIST' }
      ]
    }),
    
    // Mettre à jour les informations de paiement d'un compte (lier à un compte bancaire)
    updatePaymentInfo: builder.mutation({
      query: ({ accountId, bankAccountId }) => ({
        url: `/bank-management/red-accounts/${accountId}/link`,
        method: 'PUT',
        body: { bankAccountId }
      }),
      invalidatesTags: (result, error, { accountId }) => [
        { type: 'RedAccount', id: accountId },
        { type: 'RedAccount', id: 'LIST' },
        { type: 'BankAccount', id: 'LIST' }
      ]
    }),

    // Suggérer des lignes possibles pour une carte SIM reçue
    suggestLinesForSim: builder.mutation({
      query: ({ simReceivedDate, marginDays = 2 }) => ({
        url: `/phones/suggest-lines-for-sim`,
        method: 'POST',
        body: { simReceivedDate, marginDays }
      })
    })
  })
});

// Export hooks générées automatiquement par RTK Query
export const {
  useGetRedAccountsQuery,
  useGetRedAccountsByAgencyQuery,
  useGetRedAccountByIdQuery,
  useCreateRedAccountMutation,
  useCreateLineMutation,
  useChangeLineStatusMutation,
  useAssignLineMutation,
  useTerminateLineMutation,
  useOrderSimCardMutation,
  useUpdatePaymentInfoMutation,
  useSuggestLinesForSimMutation
} = redAccountsApiSlice;

// Slice local pour la gestion de l'état UI
const redAccountsSlice = createSlice({
  name: 'redAccounts',
  initialState: {
    selectedAccount: null,
    filter: null
  },
  reducers: {
    selectAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
    clearSelection: (state) => {
      state.selectedAccount = null;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    clearFilter: (state) => {
      state.filter = null;
    }
  }
});

// Exportation des actions et du reducer
export const { selectAccount, clearSelection, setFilter, clearFilter } = redAccountsSlice.actions;
export default redAccountsSlice.reducer;

// Sélecteurs personnalisés utilisant RTK Query
// Ils utilisent les données mises en cache par RTK Query

// Sélectionner les comptes avec disponibilité
export const selectAvailableAccounts = (state, agencyId = null) => {
  const { data: accounts = [] } = redAccountsApiSlice.endpoints.getRedAccounts.select()(state);
  const { availableAccounts } = analyzeAccountsAvailability(accounts, agencyId);
  return availableAccounts;
};

// Sélectionner les lignes résiliées disponibles
export const selectTerminatedLines = (state) => {
  const { data: accounts = [] } = redAccountsApiSlice.endpoints.getRedAccounts.select()(state);
  const { terminatedLines } = analyzeAccountsAvailability(accounts);
  return terminatedLines;
};

// Sélectionner les lignes non attribuées
export const selectUnattributedLines = (state) => {
  const { data: accounts = [] } = redAccountsApiSlice.endpoints.getRedAccounts.select()(state);
  const lines = [];
  
  accounts.forEach(account => {
    account.lines?.forEach(line => {
      if (line.status === LINE_STATUSES.UNATTRIBUTED) {
        lines.push({ ...line, accountId: account.id });
      }
    });
  });
  
  return lines;
};

// Sélectionner le compte choisi
export const selectSelectedAccount = (state) => state.redAccounts.selectedAccount;

// Fonctions utilitaires pour transformer les résultats des requêtes
export const transformAccount = (account) => {
  if (!account) return null;
  
  // Calcul des lignes disponibles, etc.
  const terminatedLines = account.lines?.filter(line => 
    line.status === LINE_STATUSES.TERMINATED && 
    isTerminatedLineAvailable(line.terminatedAt)
  ) || [];
  
  return {
    ...account,
    availableSlots: account.maxLines - account.activeLines,
    terminatedLines,
    canAcceptNewLine: account.activeLines < account.maxLines,
    availableLines: [...(account.lines?.filter(line => 
      line.status === LINE_STATUSES.UNATTRIBUTED) || []), ...terminatedLines]
  };
};